import { useCallback } from 'react';
import useSWR, { useSWRConfig } from 'swr';

import { useKnowledgeEnabled } from '~/components/knowledge/useKnowledgeEnabled';
import { carsKnowledgeList } from '~/data/generated/endpoints/cars/cars';
import {
  propertiesKnowledgeCreate,
  propertiesKnowledgeDestroy,
  propertiesKnowledgeList,
  propertiesKnowledgePartialUpdate,
  propertiesKnowledgeRestore,
} from '~/data/generated/endpoints/properties/properties';
import type {
  KnowledgeEntry,
  KnowledgeEntryCreateRequest,
  PatchedKnowledgeEntryUpdateRequest,
} from '~/data/generated/model';
import { CarsKnowledgeListResponse } from '~/data/generated/zod/cars/cars.zod';
import { PropertiesKnowledgeListResponse } from '~/data/generated/zod/properties/properties.zod';
import { useRevalidateTasks } from '~/data/hooks/taskKeys';
import { parseApiResponse } from '~/data/parseApiResponse';

const KNOWLEDGE_KEY_PREFIX = 'knowledge';

const isKnowledgeKey = (key: unknown) => Array.isArray(key) && key[0] === KNOWLEDGE_KEY_PREFIX;

const newestFirst = (entries: KnowledgeEntry[]) =>
  [...entries].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

export function usePropertyKnowledge(propertyId: string | null | undefined) {
  const enabled = useKnowledgeEnabled();
  const { data, error, isLoading } = useSWR(
    enabled && propertyId ? ([KNOWLEDGE_KEY_PREFIX, 'property', propertyId] as const) : null,
    async ([, , id]) =>
      newestFirst(
        parseApiResponse(
          PropertiesKnowledgeListResponse,
          (await propertiesKnowledgeList(id)).data,
          'KnowledgeEntry',
        ),
      ),
  );
  return { entries: data ?? [], error, isLoading };
}

export function useCarKnowledge(carId: string | null | undefined) {
  const enabled = useKnowledgeEnabled();
  const { data, error, isLoading } = useSWR(
    enabled && carId ? ([KNOWLEDGE_KEY_PREFIX, 'car', carId] as const) : null,
    async ([, , id]) =>
      newestFirst(
        parseApiResponse(
          CarsKnowledgeListResponse,
          (await carsKnowledgeList(id)).data,
          'KnowledgeEntry',
        ),
      ),
  );
  return { entries: data ?? [], error, isLoading };
}

export function useRevalidateKnowledge() {
  const { mutate } = useSWRConfig();
  return useCallback(() => mutate(isKnowledgeKey), [mutate]);
}

export function useSaveKnowledge() {
  const revalidate = useRevalidateKnowledge();
  const revalidateTasks = useRevalidateTasks();
  return useCallback(
    async (propertyId: string, body: KnowledgeEntryCreateRequest) => {
      const { data } = await propertiesKnowledgeCreate(propertyId, body);
      await Promise.all([revalidate(), data.followUpTask ? revalidateTasks() : null]);
      return data;
    },
    [revalidate, revalidateTasks],
  );
}

export function useUpdateKnowledge() {
  const revalidate = useRevalidateKnowledge();
  return useCallback(
    async (entry: KnowledgeEntry, body: PatchedKnowledgeEntryUpdateRequest) => {
      const { data } = await propertiesKnowledgePartialUpdate(entry.propertyId, entry.id, body);
      await revalidate();
      return data;
    },
    [revalidate],
  );
}

export function useDeleteKnowledge() {
  const revalidate = useRevalidateKnowledge();
  return useCallback(
    async (entry: KnowledgeEntry) => {
      await propertiesKnowledgeDestroy(entry.propertyId, entry.id);
      await revalidate();
    },
    [revalidate],
  );
}

export function useRestoreKnowledge() {
  const revalidate = useRevalidateKnowledge();
  return useCallback(
    async (entry: KnowledgeEntry) => {
      const { data } = await propertiesKnowledgeRestore(entry.propertyId, entry.id);
      await revalidate();
      return data;
    },
    [revalidate],
  );
}
